import { useEffect, useMemo, useState } from 'react';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { useAuth } from '../lib/AuthContext';
import { useSession } from '../lib/SessionContext';
import { getCurrentRedirectTo } from '../lib/funnelContext';
import {
    buildRouteWithNewsletterState,
    hasNewsletterSubscribeError,
    hasNewsletterSubscribeRequest,
    hasNewsletterSubscribeSuccess,
} from '../lib/newsletterSubscription';
import { supabase } from '../lib/supabase';
import { canAccessMainApp } from '../utils/authHelpers';

type NewsletterSubscriptionStatus = 'idle' | 'processing' | 'subscribed' | 'error';

interface UseNewsletterSubscriptionResult {
    status: NewsletterSubscriptionStatus;
    startSubscription: () => void;
    clearStatus: () => void;
    shouldRenderSubscribeButton: boolean;
}

export function useNewsletterSubscription(): UseNewsletterSubscriptionResult {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useLocalSearchParams() as Record<string, string | string[] | undefined>;
    const { session, profile, isProfileLoading, refreshProfile } = useSession();
    const { openAuth } = useAuth();
    const [isUpdatingPreference, setIsUpdatingPreference] = useState(false);

    const subscribeRequested = hasNewsletterSubscribeRequest(searchParams);
    const subscribeSucceeded = hasNewsletterSubscribeSuccess(searchParams);
    const subscribeFailed = hasNewsletterSubscribeError(searchParams);
    const isAlreadySubscribed = profile?.newsletter_opt_out === false;
    const canUpdatePreference = canAccessMainApp(session) && Boolean(session?.user?.id);
    const userId = session?.user?.id || null;
    const isAuthenticated = canAccessMainApp(session);

    useEffect(() => {
        if (!subscribeRequested || !canUpdatePreference || !userId) {
            return;
        }

        let cancelled = false;

        const applyNewsletterSubscription = async () => {
            if (isAlreadySubscribed) {
                const currentRoute = getCurrentRedirectTo(pathname || '/');
                const nextRoute = buildRouteWithNewsletterState(currentRoute, 'success');
                router.replace(nextRoute);
                return;
            }

            setIsUpdatingPreference(true);

            const { error } = await supabase
                .from('profiles')
                .update({ newsletter_opt_out: false })
                .eq('id', userId);

            if (cancelled) {
                return;
            }

            if (error) {
                console.error('🔐 [Newsletter] Failed to update newsletter preference:', error);
            } else {
                await refreshProfile();
            }

            const currentRoute = getCurrentRedirectTo(pathname || '/');
            const nextRoute = buildRouteWithNewsletterState(
                currentRoute,
                error ? 'error' : 'success'
            );
            router.replace(nextRoute);
            setIsUpdatingPreference(false);
        };

        void applyNewsletterSubscription();

        return () => {
            cancelled = true;
        };
    }, [canUpdatePreference, isAlreadySubscribed, pathname, refreshProfile, router, subscribeRequested, userId]);

    const startSubscription = () => {
        const currentRoute = getCurrentRedirectTo(pathname || '/');
        const redirectTo = buildRouteWithNewsletterState(currentRoute, 'request');

        if (canAccessMainApp(session)) {
            router.replace(redirectTo);
            return;
        }

        openAuth({
            mode: 'signup',
            redirectTo,
        });
    };

    const clearStatus = () => {
        const currentRoute = getCurrentRedirectTo(pathname || '/');
        const nextRoute = buildRouteWithNewsletterState(currentRoute, 'clear');
        router.replace(nextRoute);
    };

    const status = useMemo<NewsletterSubscriptionStatus>(() => {
        if (isUpdatingPreference || (subscribeRequested && !isAlreadySubscribed)) {
            return 'processing';
        }

        if (subscribeSucceeded) {
            return 'subscribed';
        }

        if (subscribeFailed) {
            return 'error';
        }

        return 'idle';
    }, [isAlreadySubscribed, isUpdatingPreference, subscribeFailed, subscribeRequested, subscribeSucceeded]);

    const shouldRenderSubscribeButton = useMemo(() => {
        if (isAlreadySubscribed) {
            return false;
        }

        if (status === 'subscribed') {
            return false;
        }

        if (isAuthenticated && isProfileLoading) {
            return false;
        }

        return true;
    }, [isAlreadySubscribed, isAuthenticated, isProfileLoading, status]);

    return {
        status,
        startSubscription,
        clearStatus,
        shouldRenderSubscribeButton,
    };
}
